/**
 * Default translations for the Pritect Beacon consent banner.
 *
 * Bundled with the consent script so customers get correct translations
 * out of the box. Customers can override any string via the `custom_copy`
 * JSONB field in their banner configuration.
 *
 * Keys:
 *   title           — Banner heading
 *   body            — GDPR consent body text
 *   bodyOptOut      — CCPA opt-out body text
 *   acceptAll       — Accept all button
 *   rejectAll       — GDPR reject all button
 *   rejectAllOptOut — CCPA opt-out button
 *   manage          — Manage preferences button
 *   savePrefs       — Save preferences button
 *   close           — Close button aria-label
 *   alwaysActive    — Strictly necessary toggle aria-label
 *   showVendors     — Show vendor list button
 *   hideVendors     — Hide vendor list button
 *   vendorCount     — Vendor list heading (use {count} placeholder)
 *   policy          — Vendor policy link text
 *   poweredBy       — Powered by text
 *   privacySettings — Preference center button label
 *   cat_*           — Category labels
 *   catDesc_*       — Category descriptions
 */

export interface TranslationStrings {
  title: string;
  body: string;
  bodyOptOut: string;
  acceptAll: string;
  rejectAll: string;
  rejectAllOptOut: string;
  manage: string;
  savePrefs: string;
  close: string;
  alwaysActive: string;
  showVendors: string;
  hideVendors: string;
  vendorCount: string;
  policy: string;
  poweredBy: string;
  privacySettings: string;
  cat_strictly_necessary: string;
  cat_analytics: string;
  cat_advertising: string;
  cat_functional: string;
  cat_social: string;
  catDesc_strictly_necessary: string;
  catDesc_analytics: string;
  catDesc_advertising: string;
  catDesc_functional: string;
  catDesc_social: string;
  yourCurrentState: string;
  showDetails: string;
  hideDetails: string;
  consentDate: string;
  consentId: string;
  cat_unclassified: string;
  catDesc_unclassified: string;
  declTitle: string;
  declCookieCount: string;
  declCookieCountOne: string;
  declLastScanned: string;
  declNever: string;
  declColCookie: string;
  declColProvider: string;
  declColPurpose: string;
  declColDuration: string;
  declColType: string;
  declColLawfulBasis: string;
  declFirstParty: string;
  declThirdParty: string;
  declSession: string;
  declEmpty: string;
  declError: string;
  declUpdated: string;
}

export const defaultTranslations: Record<string, TranslationStrings> = {
  en: {
    title: "Your Privacy Choices",
    body: "This site uses cookies to work properly. Some are optional and need your consent. Please choose which cookies you accept.",
    bodyOptOut: "We use cookies and similar technologies to enhance your experience. You can opt out of non-essential data collection below.",
    acceptAll: "Accept All",
    rejectAll: "Reject All",
    rejectAllOptOut: "Opt Out",
    manage: "Manage Preferences",
    savePrefs: "Save Preferences",
    close: "Close",
    alwaysActive: "Always active",
    showVendors: "Show Vendor List",
    hideVendors: "Hide Vendor List",
    vendorCount: "Vendor List ({count} vendors)",
    policy: "Policy",
    poweredBy: "Powered by",
    privacySettings: "Privacy Settings",
    cat_strictly_necessary: "Strictly Necessary",
    cat_analytics: "Analytics",
    cat_advertising: "Advertising",
    cat_functional: "Functional",
    cat_social: "Social Media",
    catDesc_strictly_necessary: "Required for the website to function. Cannot be disabled.",
    catDesc_analytics: "Help us understand how visitors interact with our website.",
    catDesc_advertising: "Used to show relevant ads and track ad campaign effectiveness.",
    catDesc_functional: "Enable enhanced features like live chat and saved preferences.",
    catDesc_social: "Allow social media sharing and embedding.",
    yourCurrentState: "Your current state",
    showDetails: "Show details",
    hideDetails: "Hide details",
    consentDate: "Consent date",
    consentId: "Consent ID",
    cat_unclassified: "Unclassified",
    catDesc_unclassified: "Cookies that have not yet been categorized.",
    declTitle: "Cookie Declaration",
    declCookieCount: "{count} cookies in use on {domain}",
    declCookieCountOne: "1 cookie in use on {domain}",
    declLastScanned: "Last scanned",
    declNever: "Never",
    declColCookie: "Cookie",
    declColProvider: "Provider",
    declColPurpose: "Purpose",
    declColDuration: "Duration",
    declColType: "Type",
    declColLawfulBasis: "Lawful Basis",
    declFirstParty: "1st party",
    declThirdParty: "3rd party",
    declSession: "Session",
    declEmpty: "No cookies have been discovered yet. Run a scan to populate this declaration.",
    declError: "Cookie declaration unavailable. Please try again later.",
    declUpdated: "Updated",
  },

  fr: {
    title: "Vos choix en matière de confidentialité",
    body: "Ce site utilise des cookies pour fonctionner correctement. Certains sont facultatifs et nécessitent votre consentement. Veuillez choisir les cookies que vous acceptez.",
    bodyOptOut: "Nous utilisons des cookies et des technologies similaires pour améliorer votre expérience. Vous pouvez refuser la collecte de données non essentielles ci-dessous.",
    acceptAll: "Tout accepter",
    rejectAll: "Tout refuser",
    rejectAllOptOut: "Refuser",
    manage: "Gérer les préférences",
    savePrefs: "Enregistrer les préférences",
    close: "Fermer",
    alwaysActive: "Toujours actif",
    showVendors: "Afficher la liste des fournisseurs",
    hideVendors: "Masquer la liste des fournisseurs",
    vendorCount: "Liste des fournisseurs ({count} fournisseurs)",
    policy: "Politique",
    poweredBy: "Propulsé par",
    privacySettings: "Paramètres de confidentialité",
    cat_strictly_necessary: "Strictement nécessaires",
    cat_analytics: "Analytiques",
    cat_advertising: "Publicitaires",
    cat_functional: "Fonctionnels",
    cat_social: "Réseaux sociaux",
    catDesc_strictly_necessary: "Nécessaires au fonctionnement du site. Ne peuvent pas être désactivés.",
    catDesc_analytics: "Nous aident à comprendre comment les visiteurs interagissent avec notre site.",
    catDesc_advertising: "Utilisés pour afficher des publicités pertinentes et mesurer l'efficacité des campagnes.",
    catDesc_functional: "Permettent des fonctionnalités avancées comme le chat en direct et les préférences enregistrées.",
    catDesc_social: "Permettent le partage et l'intégration de contenu sur les réseaux sociaux.",
    yourCurrentState: "Votre état actuel",
    showDetails: "Afficher les détails",
    hideDetails: "Masquer les détails",
    consentDate: "Date du consentement",
    consentId: "ID de consentement",
    cat_unclassified: "Non classés",
    catDesc_unclassified: "Cookies qui n'ont pas encore été catégorisés.",
    declTitle: "Déclaration des cookies",
    declCookieCount: "{count} cookies utilisés sur {domain}",
    declCookieCountOne: "1 cookie utilisé sur {domain}",
    declLastScanned: "Dernière analyse",
    declNever: "Jamais",
    declColCookie: "Cookie",
    declColProvider: "Fournisseur",
    declColPurpose: "Finalité",
    declColDuration: "Durée",
    declColType: "Type",
    declColLawfulBasis: "Base juridique",
    declFirstParty: "1re partie",
    declThirdParty: "Tierce partie",
    declSession: "Session",
    declEmpty: "Aucun cookie n'a encore été découvert. Lancez une analyse pour remplir cette déclaration.",
    declError: "Déclaration des cookies indisponible. Veuillez réessayer plus tard.",
    declUpdated: "Mis à jour le",
  },

  de: {
    title: "Ihre Datenschutzeinstellungen",
    body: "Diese Website verwendet Cookies, um zu funktionieren. Einige sind optional und erfordern Ihre Einwilligung. Bitte wählen Sie, welche Cookies Sie akzeptieren.",
    bodyOptOut: "Wir verwenden Cookies und ähnliche Technologien, um Ihre Erfahrung zu verbessern. Sie können die nicht wesentliche Datenerfassung unten ablehnen.",
    acceptAll: "Alle akzeptieren",
    rejectAll: "Alle ablehnen",
    rejectAllOptOut: "Ablehnen",
    manage: "Einstellungen verwalten",
    savePrefs: "Einstellungen speichern",
    close: "Schließen",
    alwaysActive: "Immer aktiv",
    showVendors: "Anbieterliste anzeigen",
    hideVendors: "Anbieterliste ausblenden",
    vendorCount: "Anbieterliste ({count} Anbieter)",
    policy: "Richtlinie",
    poweredBy: "Betrieben von",
    privacySettings: "Datenschutzeinstellungen",
    cat_strictly_necessary: "Unbedingt erforderlich",
    cat_analytics: "Analyse",
    cat_advertising: "Werbung",
    cat_functional: "Funktional",
    cat_social: "Soziale Medien",
    catDesc_strictly_necessary: "Für die Funktion der Website erforderlich. Können nicht deaktiviert werden.",
    catDesc_analytics: "Helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.",
    catDesc_advertising: "Werden verwendet, um relevante Werbung anzuzeigen und die Wirksamkeit von Kampagnen zu messen.",
    catDesc_functional: "Ermöglichen erweiterte Funktionen wie Live-Chat und gespeicherte Einstellungen.",
    catDesc_social: "Ermöglichen das Teilen und Einbetten von Inhalten in sozialen Medien.",
    yourCurrentState: "Ihr aktueller Status",
    showDetails: "Details anzeigen",
    hideDetails: "Details ausblenden",
    consentDate: "Einwilligungsdatum",
    consentId: "Einwilligungs-ID",
    cat_unclassified: "Nicht klassifiziert",
    catDesc_unclassified: "Cookies, die noch nicht kategorisiert wurden.",
    declTitle: "Cookie-Erklärung",
    declCookieCount: "{count} Cookies auf {domain} im Einsatz",
    declCookieCountOne: "1 Cookie auf {domain} im Einsatz",
    declLastScanned: "Zuletzt gescannt",
    declNever: "Nie",
    declColCookie: "Cookie",
    declColProvider: "Anbieter",
    declColPurpose: "Zweck",
    declColDuration: "Dauer",
    declColType: "Typ",
    declColLawfulBasis: "Rechtsgrundlage",
    declFirstParty: "Erstanbieter",
    declThirdParty: "Drittanbieter",
    declSession: "Sitzung",
    declEmpty: "Es wurden noch keine Cookies entdeckt. Führen Sie einen Scan durch, um diese Erklärung zu füllen.",
    declError: "Cookie-Erklärung nicht verfügbar. Bitte versuchen Sie es später erneut.",
    declUpdated: "Aktualisiert",
  },

  es: {
    title: "Sus opciones de privacidad",
    body: "Este sitio utiliza cookies para funcionar correctamente. Algunas son opcionales y requieren su consentimiento. Elija qué cookies acepta.",
    bodyOptOut: "Utilizamos cookies y tecnologías similares para mejorar su experiencia. Puede rechazar la recopilación de datos no esenciales a continuación.",
    acceptAll: "Aceptar todas",
    rejectAll: "Rechazar todas",
    rejectAllOptOut: "Rechazar",
    manage: "Gestionar preferencias",
    savePrefs: "Guardar preferencias",
    close: "Cerrar",
    alwaysActive: "Siempre activo",
    showVendors: "Mostrar lista de proveedores",
    hideVendors: "Ocultar lista de proveedores",
    vendorCount: "Lista de proveedores ({count} proveedores)",
    policy: "Política",
    poweredBy: "Desarrollado por",
    privacySettings: "Configuración de privacidad",
    cat_strictly_necessary: "Estrictamente necesarias",
    cat_analytics: "Analíticas",
    cat_advertising: "Publicitarias",
    cat_functional: "Funcionales",
    cat_social: "Redes sociales",
    catDesc_strictly_necessary: "Necesarias para el funcionamiento del sitio web. No se pueden desactivar.",
    catDesc_analytics: "Nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web.",
    catDesc_advertising: "Se utilizan para mostrar anuncios relevantes y medir la efectividad de las campañas.",
    catDesc_functional: "Habilitan funciones avanzadas como el chat en vivo y las preferencias guardadas.",
    catDesc_social: "Permiten compartir e integrar contenido en redes sociales.",
    yourCurrentState: "Su estado actual",
    showDetails: "Mostrar detalles",
    hideDetails: "Ocultar detalles",
    consentDate: "Fecha de consentimiento",
    consentId: "ID de consentimiento",
    cat_unclassified: "Sin clasificar",
    catDesc_unclassified: "Cookies que aún no han sido categorizadas.",
    declTitle: "Declaración de cookies",
    declCookieCount: "{count} cookies en uso en {domain}",
    declCookieCountOne: "1 cookie en uso en {domain}",
    declLastScanned: "Último análisis",
    declNever: "Nunca",
    declColCookie: "Cookie",
    declColProvider: "Proveedor",
    declColPurpose: "Finalidad",
    declColDuration: "Duración",
    declColType: "Tipo",
    declColLawfulBasis: "Base jurídica",
    declFirstParty: "Primera parte",
    declThirdParty: "Terceros",
    declSession: "Sesión",
    declEmpty: "Aún no se han descubierto cookies. Ejecute un análisis para completar esta declaración.",
    declError: "Declaración de cookies no disponible. Inténtelo de nuevo más tarde.",
    declUpdated: "Actualizado",
  },

  it: {
    title: "Le tue scelte sulla privacy",
    body: "Questo sito utilizza i cookie per funzionare correttamente. Alcuni sono facoltativi e richiedono il tuo consenso. Scegli quali cookie accetti.",
    bodyOptOut: "Utilizziamo cookie e tecnologie simili per migliorare la tua esperienza. Puoi rifiutare la raccolta di dati non essenziali qui sotto.",
    acceptAll: "Accetta tutti",
    rejectAll: "Rifiuta tutti",
    rejectAllOptOut: "Rifiuta",
    manage: "Gestisci preferenze",
    savePrefs: "Salva preferenze",
    close: "Chiudi",
    alwaysActive: "Sempre attivo",
    showVendors: "Mostra elenco fornitori",
    hideVendors: "Nascondi elenco fornitori",
    vendorCount: "Elenco fornitori ({count} fornitori)",
    policy: "Informativa",
    poweredBy: "Offerto da",
    privacySettings: "Impostazioni sulla privacy",
    cat_strictly_necessary: "Strettamente necessari",
    cat_analytics: "Analitici",
    cat_advertising: "Pubblicitari",
    cat_functional: "Funzionali",
    cat_social: "Social media",
    catDesc_strictly_necessary: "Necessari per il funzionamento del sito. Non possono essere disattivati.",
    catDesc_analytics: "Ci aiutano a capire come i visitatori interagiscono con il nostro sito.",
    catDesc_advertising: "Utilizzati per mostrare annunci pertinenti e misurare l'efficacia delle campagne.",
    catDesc_functional: "Abilitano funzionalità avanzate come la chat dal vivo e le preferenze salvate.",
    catDesc_social: "Consentono la condivisione e l'integrazione di contenuti sui social media.",
    yourCurrentState: "Il tuo stato attuale",
    showDetails: "Mostra dettagli",
    hideDetails: "Nascondi dettagli",
    consentDate: "Data del consenso",
    consentId: "ID del consenso",
    cat_unclassified: "Non classificati",
    catDesc_unclassified: "Cookie non ancora categorizzati.",
    declTitle: "Dichiarazione sui cookie",
    declCookieCount: "{count} cookie in uso su {domain}",
    declCookieCountOne: "1 cookie in uso su {domain}",
    declLastScanned: "Ultima scansione",
    declNever: "Mai",
    declColCookie: "Cookie",
    declColProvider: "Fornitore",
    declColPurpose: "Finalità",
    declColDuration: "Durata",
    declColType: "Tipo",
    declColLawfulBasis: "Base giuridica",
    declFirstParty: "Prima parte",
    declThirdParty: "Terze parti",
    declSession: "Sessione",
    declEmpty: "Nessun cookie è stato ancora rilevato. Esegui una scansione per compilare questa dichiarazione.",
    declError: "Dichiarazione sui cookie non disponibile. Riprova più tardi.",
    declUpdated: "Aggiornato",
  },

  ar: {
    title: "خيارات الخصوصية الخاصة بك",
    body: "يستخدم هذا الموقع ملفات تعريف الارتباط لكي يعمل بشكل صحيح. بعضها اختياري ويتطلب موافقتك. يرجى اختيار ملفات تعريف الارتباط التي تقبلها.",
    bodyOptOut: "نستخدم ملفات تعريف الارتباط وتقنيات مماثلة لتحسين تجربتك. يمكنك رفض جمع البيانات غير الأساسية أدناه.",
    acceptAll: "قبول الكل",
    rejectAll: "رفض الكل",
    rejectAllOptOut: "رفض",
    manage: "إدارة التفضيلات",
    savePrefs: "حفظ التفضيلات",
    close: "إغلاق",
    alwaysActive: "نشط دائمًا",
    showVendors: "عرض قائمة المزودين",
    hideVendors: "إخفاء قائمة المزودين",
    vendorCount: "قائمة المزودين ({count} مزود)",
    policy: "السياسة",
    poweredBy: "مدعوم من",
    privacySettings: "إعدادات الخصوصية",
    cat_strictly_necessary: "ضرورية للغاية",
    cat_analytics: "تحليلية",
    cat_advertising: "إعلانية",
    cat_functional: "وظيفية",
    cat_social: "وسائل التواصل الاجتماعي",
    catDesc_strictly_necessary: "مطلوبة لعمل الموقع. لا يمكن تعطيلها.",
    catDesc_analytics: "تساعدنا على فهم كيفية تفاعل الزوار مع موقعنا.",
    catDesc_advertising: "تُستخدم لعرض إعلانات ذات صلة وقياس فعالية الحملات الإعلانية.",
    catDesc_functional: "تُمكّن الميزات المتقدمة مثل الدردشة المباشرة والتفضيلات المحفوظة.",
    catDesc_social: "تسمح بمشاركة المحتوى وتضمينه على وسائل التواصل الاجتماعي.",
    yourCurrentState: "حالتك الحالية",
    showDetails: "عرض التفاصيل",
    hideDetails: "إخفاء التفاصيل",
    consentDate: "تاريخ الموافقة",
    consentId: "معرّف الموافقة",
    cat_unclassified: "غير مصنفة",
    catDesc_unclassified: "ملفات تعريف ارتباط لم يتم تصنيفها بعد.",
    declTitle: "بيان ملفات تعريف الارتباط",
    declCookieCount: "{count} ملفات تعريف ارتباط مستخدمة على {domain}",
    declCookieCountOne: "ملف تعريف ارتباط واحد مستخدم على {domain}",
    declLastScanned: "آخر فحص",
    declNever: "أبدًا",
    declColCookie: "ملف تعريف الارتباط",
    declColProvider: "المزود",
    declColPurpose: "الغرض",
    declColDuration: "المدة",
    declColType: "النوع",
    declColLawfulBasis: "الأساس القانوني",
    declFirstParty: "طرف أول",
    declThirdParty: "طرف ثالث",
    declSession: "جلسة",
    declEmpty: "لم يتم اكتشاف أي ملفات تعريف ارتباط بعد. قم بإجراء فحص لملء هذا البيان.",
    declError: "بيان ملفات تعريف الارتباط غير متاح. يرجى المحاولة مرة أخرى لاحقًا.",
    declUpdated: "تم التحديث",
  },
};

/** RTL languages that require direction: rtl on the banner container */
export const RTL_LANGUAGES = new Set(["ar", "he", "fa", "ur"]);

/**
 * Resolve a translation string with 3-layer fallback:
 * 1. Customer override (custom_copy[lang][key])
 * 2. Bundled default (defaultTranslations[lang][key])
 * 3. English fallback (defaultTranslations["en"][key])
 */
export function resolveTranslation(
  key: keyof TranslationStrings,
  lang: string,
  customCopy?: Record<string, Record<string, string>> | null,
): string {
  const custom = customCopy?.[lang]?.[key];
  if (custom) return custom;

  const bundled = defaultTranslations[lang]?.[key];
  if (bundled) return bundled;

  return defaultTranslations["en"][key];
}
